// © Microsoft Corporation. All rights reserved.
// Provides utility functions to get and set data from browser webStorage.

import { tryParseJson } from "../utilities/Parsing";
import { AppError, CoreLibAppErrors, logger } from "@msnews/diagnostics";
import { canUseDOM } from "@msnews/isomorphic-util";

/**
 * Base class for localeStorage and sessionStorage abstractions
 */
export class WebStorage {
    /**
     * Local storage object for persistent storage
     */
    constructor(private readonly storage: Storage) {
    }

    /**
     * Check if the web storage is supported
     * @returns {boolean} true if supported
     */
    public get supported(): boolean {
        return !!this.storage;
    }

    /**
     * Get given key's value from web storage
     * @param {string} key - Key name
     * @returns {string} Key value
     */
    public getItem(key: string): string {
        if (this.supported) {
            return this.storage.getItem(key);
        }
    }

    /**
     * Gets the value from local storage using the key.
     * @param {string} key - the storage key to store under
     * @param {T} defaultValue - the default value to return if parsing fails
     * @returns {T} The parsed JSON value
     */
    public getObject<T>(key: string, defaultValue?: T): T {
        let value = this.getItem(key);
        if (value != null) {
            let parsedObj = tryParseJson(value);
            if (parsedObj != null) {
                return <T>parsedObj;
            }
        }

        return defaultValue;
    }

    /**
     * Gets given index location key from web storage
     * @param {string} key - Key name
     * @returns {string} Key value
     */
    public key(index: number): string {
        if (this.supported && index >= 0) {
            return this.storage.key(index);
        }
    }

    /**
     * Sets the given value after stringifying as JSON
     * @param {string} key - the storage key to store under
     * @param {T} value - the value to serialize and store
     */
    public setObject<T>(key: string, value: T): void {
        (value !== undefined)
            ? this.setItem(key, JSON.stringify(value))
            : this.removeItem(key);
    }

    /**
     * Removes the given key from storage and returns the existing value parsed
     * @param {string} key - the key to remove.
     */
    public removeObject<T>(key: string): T {
        let value = this.removeItem(key);
        if (value != null) {
            return <T>tryParseJson(value);
        }
    }

    /**
     * Set given key's value to local storage
     * @param {string} key - Key name
     * @param {string} value - Key value
     */
    public setItem(key: string, value: string): void {
        if (this.supported) {
            // Catch quota exceeded exception
            try {
                this.storage.setItem(key, value);
            } catch (e) {
                logger.logError(e);
            }
        }
    }

    /**
     * Remove key item from local storage
     * @param {string} key - Key name
     * @returns {string} Key value
     */
    public removeItem(key: string): string {
        if (this.supported) {
            let value = this.getItem(key);
            this.storage.removeItem(key);
            return value;
        }
    }

    /**
     * Removes all keys containing the given substring in the storage key
     * @param {string} substring The substring to check
     */
    public removeSubstringKeys(substring: string): void {
        if (!this.supported || !substring) {
            return;
        }

        let matchingKeys = [];
        for (let index = 0; index < this.storage.length; index++) {
            const storageKey = this.key(index);
            if (storageKey && storageKey.includes(substring)) {
                matchingKeys.push(storageKey);
            }
        }

        for (let index = 0; index < matchingKeys.length; index++) {
            this.removeItem(matchingKeys[index]);
        }
    }
}

/**
 * Local storage class for persistent storage
 */
export class LocalStorage extends WebStorage {
    constructor() {
        let storage: Storage;

        try {
            storage = "localStorage" in window && window.localStorage;
        } catch (ex) {
            // Getting local storage failed. Can happen in Chrome when user has disabled cookies.
        }

        super(storage);
    }

    /**
    * Checks whether localStorage is available or not.
    * Need to aggressively check because iOS5 & Safari Private Browsing mode will throw error.
    * This is how the library Modernizr does localStorage check. (MIT License)
    * For more details:
    * https://github.com/Modernizr/Modernizr/blob/master/feature-detects/storage/localstorage.js
    */
    public localStorageExists(trackAppErrorEvent: (data: AppError) => void): boolean {
        if(canUseDOM()){
            var storageTest = "storageTest";
            try {
                window.localStorage.setItem(storageTest, storageTest);
                window.localStorage.removeItem(storageTest);
                return true;
            } catch(e) {
                if (trackAppErrorEvent || typeof trackAppErrorEvent === "function") {
                    trackAppErrorEvent({
                        ...CoreLibAppErrors.LocalStorageDoesntExist,
                        message: `Local Storage is not defined ${e}`
                    });
                }
            }
        }
        return false;
    }
}

/**
 * Session storage class for persistent storage
 */
export class SessionStorage extends WebStorage {
    constructor() {
        let storage: Storage;

        try {
            storage = "sessionStorage" in window && window.sessionStorage;
        } catch (ex) {
            // Getting session storage failed. Can happen in Chrome when user has disabled cookies.
        }

        super(storage);
    }

    /**
    * Checks whether sessionStorage is available or not.
    * Need to aggressively check because iOS5 & Safari Private Browsing mode will throw error.
    * This is how the library Modernizr does sessionStorage check. (MIT License)
    * For more details:
    * https://github.com/Modernizr/Modernizr/blob/master/feature-detects/storage/sessionstorage.js
    */
    public sessionStorageExists(trackAppErrorEvent: (data: AppError) => void): boolean {
        if(canUseDOM()){
            var storageTest = "storageTest";
            try {
                window.sessionStorage.setItem(storageTest, storageTest);
                window.sessionStorage.removeItem(storageTest);
                return true;
            } catch(e) {
                if (trackAppErrorEvent || typeof trackAppErrorEvent === "function") {
                    trackAppErrorEvent({
                        ...CoreLibAppErrors.SessionStorageDoesntExist,
                        message: `Session Storage is not defined: ${e}`
                    });
                }
            }
        } 
        return false;
    }
}

export const localStorage = new LocalStorage();
export const sessionStorage = new SessionStorage();